import { useCallback, useMemo, useState } from 'react';
import { useQuery, usePaginatedQuery, queryCache } from 'react-query';
import { message } from 'antd';

const useDisplayError = (shouldDisplayError, error, prefixErrorMessage) => {
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);

  if (!isErrorDisplayed && shouldDisplayError && !!error) {
    setIsErrorDisplayed(true);
    console.error(error);

    const baseErrorMessage = error.message || 'Something went wrong while retriving data, please contact our technical support';
    const errorMessage = `${prefixErrorMessage}${baseErrorMessage}`;
    message.error(errorMessage);
  }
};

export const useCustomQuery = (
  key,
  paramsInArray = [],
  apiFunction,
  { shouldDisplayError = true, shouldDefaultEmptyObject = true, prefixErrorMessage = '', ...queryOptions } = {}
) => {
  const keysInQuery = useMemo(() => [key, ...paramsInArray], [key, paramsInArray]);

  const {
    data,
    isFetching, // isFetching is the loading state that we always expected
    isLoading, // We deconstruct isLoading to prevent overriding our custom field when return
    error,
    ...returnedParams
  } = useQuery(keysInQuery, apiFunction, queryOptions);
  useDisplayError(shouldDisplayError, error, prefixErrorMessage);

  const returnedData = useMemo(() => data || (shouldDefaultEmptyObject ? {} : data), [shouldDefaultEmptyObject, data]);
  const refetch = useCallback(() => queryCache.invalidateQueries(keysInQuery), [keysInQuery]);
  // isFetching is alaways true even when it is not fetching.
  return { isLoading: isFetching, data: returnedData, error, refetch, ...returnedParams };
};

export const useCustomPaginatedQuery = (
  key,
  apiFunction,
  { filter, sorter, currentPage = 1, limit = 10 } = {},
  { shouldDisplayError = true, shouldDefaultEmptyObject = true, prefixErrorMessage = '', ...queryOptions } = {}
) => {
  const queryToFetch = {
    ...(filter && { filter: encodeURIComponent(JSON.stringify(filter)) }),
    ...(sorter && { sort: encodeURIComponent(JSON.stringify(sorter)) }),
    pagination: encodeURIComponent(JSON.stringify({ limit, currentPage: currentPage }))
  };

  const {
    resolvedData,
    latestData,
    error,
    isFetching, // isFetching is the loading state that we always expected
    isLoading, // We deconstruct isLoading to prevent overriding our custom field when return
    ...returnedParams
  } = usePaginatedQuery([key, filter, sorter, currentPage, limit], async () => apiFunction(key, queryToFetch), queryOptions);
  useDisplayError(shouldDisplayError, error, prefixErrorMessage);
  const refetch = useCallback(() => queryCache.invalidateQueries([key]), [key]);

  return {
    isLoading: isFetching,
    paginatedData: resolvedData && resolvedData[key],
    total: resolvedData && resolvedData.totalCount,
    error,
    latestData,
    refetch,
    ...returnedParams
  };
};
